import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiEye, FiHeart, FiMessageSquare, FiBookmark } from 'react-icons/fi';
import { generateClient } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage';
import { PageContainer } from '../App';

const client = generateClient();

// Add getS3ImageUrl helper function
const getS3ImageUrl = async (key) => {
  if (!key) return null;
  try {
    const { url } = await getUrl({ path: key });
    return url;
  } catch (err) {
    console.error('Error getting image URL:', err);
    return null;
  }
};

const BookmarkPage = () => {
  const [bookmarkedArticles, setBookmarkedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBookmarkedArticles = async () => {
      try {
        const bookmarkIds = JSON.parse(localStorage.getItem('bookmarkIds') || '[]');
        
        if (bookmarkIds.length === 0) {
          setBookmarkedArticles([]);
          setLoading(false);
          return;
        }

        const articlePromises = bookmarkIds.map(id => 
          client.models.Article.get({ id })
        );

        const articlesResponses = await Promise.all(articlePromises);
        
        // Process articles and get signed URLs for images
        const processedArticles = await Promise.all(
          articlesResponses
            .filter(response => response && response.data)
            .map(async response => {
              const article = response.data;
              const fullImageUrl = await getS3ImageUrl(article.bannerImageUrl);
              return {
                ...article,
                bannerImageUrl: fullImageUrl
              };
            })
        );
        
        setBookmarkedArticles(processedArticles);
        setError(null);
      } catch (err) {
        console.error('Error fetching bookmarked articles:', err);
        setError('Failed to load bookmarked articles');
      } finally {
        setLoading(false);
      }
    };

    fetchBookmarkedArticles();
  }, []);

  if (loading) {
    return <LoadingMessage>Loading bookmarks...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  return (
    <PageContainer>
      <BookmarkContent>
        <PageHeader>
          <PageTitle>Your Bookmarks</PageTitle>
        </PageHeader>

        {bookmarkedArticles.length === 0 ? (
          <NoBookmarks>You haven't bookmarked any articles yet.</NoBookmarks>
        ) : (
          <ArticleFeed>
            {bookmarkedArticles.map((article) => article && (
              <ArticleCard key={article.id} as={Link} to={`/article/${article.id}`}>
                {article.bannerImageUrl && (
                  <ArticleImage>
                    <img src={article.bannerImageUrl} alt={article.title} />
                  </ArticleImage>
                )}
                <ArticleCardContent>
                  <Author>
                    <AuthorImage />
                    <AuthorName>{article.author}</AuthorName>
                  </Author>
                  <ArticleTitle>
                    {article.title}
                  </ArticleTitle>
                  <ArticlePreview>
                    {article.content?.slice(0, 300) || article.description?.slice(0, 300)}
                    {((article.content && article.content.length > 300) || (article.description && article.description.length > 300)) && '...'}
                  </ArticlePreview>
                  <ArticleMeta>
                    <span>
                      {new Date(article.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })} · {article.readTime} min read
                    </span>
                    <MetricItem>
                      <FiEye /> {article.views || 0}
                    </MetricItem>
                    <MetricItem>
                      <FiHeart /> {article.likes || 0}
                    </MetricItem>
                    <MetricItem>
                      <FiMessageSquare /> {article.commentsCount || 0}
                    </MetricItem>
                    <MetricItem isBookmarked={true}>
                      <FiBookmark />
                    </MetricItem>
                  </ArticleMeta>
                </ArticleCardContent>
              </ArticleCard>
            ))}
          </ArticleFeed>
        )}
      </BookmarkContent>
    </PageContainer>
  );
};

const BookmarkContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  font-family: 'Source Serif 4', Georgia, serif;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const NoBookmarks = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: #757575;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    padding: 3rem 0;
    font-size: 1.1rem;
  }
`;

const ArticleFeed = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const ArticleCard = styled.article`
  background: white;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 0px 4px;
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  display: flex;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ArticleImage = styled.div`
  width: 280px;
  flex-shrink: 0;
  background: #f8f9fa;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 200px;
    position: relative;
  }
`;

const ArticleCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const AuthorImage = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: url('/author.jpeg');
  background-size: cover;
  background-position: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const AuthorName = styled.span`
  font-weight: 500;
  color: #292929;
`;

const ArticleTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  font-family: 'Source Serif 4', Georgia, serif;
  color: #292929;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const ArticlePreview = styled.p`
  color: #757575;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
`;

const ArticleMeta = styled.div`
  color: #757575;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const MetricItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: ${props => props.isBookmarked ? '#1a8917' : '#757575'};

  svg {
    fill: ${props => props.isBookmarked ? 'currentColor' : 'none'};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: #757575;
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: #e53935;
  font-size: 1.2rem;
`;

export default BookmarkPage; 